import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { connect, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import ACTIONS from "../store/action";
import { Typography, Button, Form, Space, Row, Col, Radio, Tooltip, Modal, Empty } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import moment from "moment";

import { NoteFormItem } from "./form/NoteFormItem";
import { CycleFormItem } from "./form/CycleFormItem";
import {
  selectIsFetching,
  selectPatientNotes,
  selectUsers,
  selectUser,
  selectSelectedOrderId,
  selectPatientCycles
} from "../store/selector";
import { ApiNames } from "../api/api";
import { DATE_FORMAT_2 } from "../utils/date";
import { getDefaultCycleNumber } from "../utils/cycles";
import { getNoteHeaderLabel, getNoteTooltipContent, formatUserName } from "../utils/editNotes";
import {
  NoteContent,
  ActionButtons,
  ActionButton,
  EditContainer,
  EditNoteTextArea,
  ButtonsContainer,
  SaveNoteButton,
  AddNoteButton,
  CancelEditButton,
  EditNoteHeader,
  EditNoteLabel,
  StyledEmptyDescription,
  StyledTable
} from "./PatientNoteTab/PharmacyNotesUI";
import { ReactComponent as WarningIcon } from "../assets/svg/warning-icon-popup.svg";
import { ReactComponent as DeleteIcon } from "../assets/svg/delete-new-icon.svg";
import { GRAY_900, WHITE } from "../constant/colors";
import { fontWeights, sizes } from "../constant/styles";
import { ExpandableNoteContent } from "./PatientNoteTab/ExpandableNoteContent";
import { ReactComponent as EditNoteIcon } from "../assets/svg/edit-note-action.svg";
import { ReactComponent as DeleteNoteIcon } from "../assets/svg/delete-note-action.svg";
import { DECEND, SORT_DIRECTIONS } from "../constant/sort";
import { dateSort, stringSort } from "../utils/sort";

const { Text } = Typography;

const NoteType = Object.freeze({
  CYCLE: "CYCLE",
  PATIENT: "PATIENT"
});

const PatientNoteTabComponent = ({
  t,
  patient,
  users,
  patientNotes,
  isFetching,
  addPatientNoteAct,
  editPatientNoteAct,
  deletePatientNoteAct,
  setEdited
}) => {
  const selectedOrderId = useSelector(selectSelectedOrderId);
  const cycles = useSelector(selectPatientCycles);
  const [noteType, setNoteType] = useState(NoteType.CYCLE);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();

  // Edit note state
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [editingOriginalText, setEditingOriginalText] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Pending note for edit confirmation
  const [pendingNoteToEdit, setPendingNoteToEdit] = useState(null);

  const isFetchingEditNote = useSelector(selectIsFetching([ApiNames.NotesEdit]));
  const isFetchingDeleteNote = useSelector(selectIsFetching([ApiNames.NoteDelete]));

  useEffect(() => {
    if (setEdited) {
      setEdited(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, setEdited]);

  const onAddNote = (values) => {
    const { currentOrder } = patient;
    const { pharmacyPatient } = currentOrder;
    addPatientNoteAct(patient.patient.id, values.note, values.cycle, pharmacyPatient);

    form.setFieldsValue({ note: "", cycle: values.cycle ? values.cycle : currentOrder.orderIndex + 1 });
    setIsFormOpen(false);
  };

  const handleEditNote = (note) => {
    // Check if already editing another note with unsaved changes
    if (editingNoteId && editingNoteId !== note.id && hasUnsavedChanges) {
      setPendingNoteToEdit(note);
      modal.confirm({
        className: "formPopupsModal",
        icon: <WarningIcon />,
        okText: t("yes_sure"),
        cancelText: t("back"),
        centered: true,
        title: t("patient.notes.unsaved_changes_title"),
        content: t("patient.notes.unsaved_changes_message"),
        onOk: () => handleDiscardChanges(note),
        onCancel: () => handleKeepEditing(),
        okButtonProps: {
          id: "yes_sure_button"
        },
        cancelButtonProps: {
          id: "back_button"
        }
      });
      return;
    }

    // If editing the same note or no unsaved changes, just switch to the new note
    setEditingNoteId(note.id);
    setEditingNoteText(note.note);
    setEditingOriginalText(note.note);
    setHasUnsavedChanges(false);
    editForm.setFieldValue(`editNoteText-${note.id}`, note.note);
  };

  const handleEditNoteTextChange = (e) => {
    const newText = e.target.value;
    const hasChanges = newText.trim() !== editingOriginalText.trim();

    setEditingNoteText(newText);
    setHasUnsavedChanges(hasChanges);
  };

  const handleSaveEdit = () => {
    if (!hasUnsavedChanges || !editingNoteText.trim()) {
      return;
    }

    const { currentOrder } = patient;
    const { pharmacyPatient } = currentOrder;

    // Call the action with correct parameters
    editPatientNoteAct(editingNoteId, editingNoteText.trim(), patient.patient.id, pharmacyPatient);

    // Reset edit state
    setEditingNoteId(null);
    setEditingNoteText("");
    setEditingOriginalText("");
    setHasUnsavedChanges(false);
    editForm.resetFields();

    // Clear parent's edited state
    if (setEdited) {
      setEdited(false);
    }
  };

  const handleCancelEdit = () => {
    resetEditState();
  };

  const resetEditState = () => {
    setEditingNoteId(null);
    setEditingNoteText("");
    setEditingOriginalText("");
    setHasUnsavedChanges(false);
    editForm.resetFields();

    // Clear parent's edited state
    if (setEdited) {
      setEdited(false);
    }
  };

  const handleDiscardChanges = (noteToEdit) => {
    const targetNote = noteToEdit || pendingNoteToEdit;

    // If there's a pending note to edit, set it directly
    if (targetNote) {
      setEditingNoteId(targetNote.id);
      setEditingNoteText(targetNote.note);
      setEditingOriginalText(targetNote.note);
      setHasUnsavedChanges(false);
      editForm.setFieldValue(`editNoteText-${targetNote.id}`, targetNote.note);

      // Clear parent's edited state since we're discarding changes
      if (setEdited) {
        setEdited(false);
      }
    } else {
      resetEditState();
    }

    setPendingNoteToEdit(null);
  };

  const handleKeepEditing = () => {
    setPendingNoteToEdit(null);
  };

  const handleDeleteClick = (note) => {
    modal.confirm({
      className: "formPopupsModal",
      icon: <DeleteIcon style={{ width: "48px" }} />,
      okText: t("keep_note"),
      cancelText: t("delete_note"),
      centered: true,
      title: t("patient.notes.confirm_delete_title"),
      content: t("patient.notes.confirm_delete_content"),
      onCancel: () => {
        // Delete note
        const { currentOrder } = patient;
        const { pharmacyPatient } = currentOrder;
        deletePatientNoteAct(patient.patient.id, note.id, pharmacyPatient);
      },
      okButtonProps: {
        id: "keep_note_button"
      },
      cancelButtonProps: {
        id: "delete_note_button"
      }
    });
  };

  // Filter out deleted notes and sort by creation date
  const displayedNotes = patientNotes
    .filter((note) => !note.isDeleted)
    .sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate));

  const columns = [
    {
      title: t("patient.notes.col_title_date"),
      dataIndex: "creationDate",
      key: "creationDate",
      width: 120,
      defaultSortOrder: DECEND,
      sortDirections: SORT_DIRECTIONS,
      sorter: (a, b, sortDirection) => dateSort(a.createdAt, b.createdAt, sortDirection),
      render: (text, record) => moment(record.createdAt).format(DATE_FORMAT_2)
    },
    {
      title: t("patient.notes.last_updated_by"),
      dataIndex: "userInfo",
      key: "userInfo",
      width: 180,
      sortDirections: SORT_DIRECTIONS,
      sorter: (a, b, sortDirection) => {
        const userA = users[a.createdBy];
        const userB = users[b.createdBy];
        const nameA = a.updatedByUserName || a.createdByUserName || formatUserName(userA);
        const nameB = b.updatedByUserName || b.createdByUserName || formatUserName(userB);
        return stringSort(nameA, nameB, sortDirection);
      },
      render: (_, note) => {
        const user = users[note.createdBy];
        const createdByUserName = note.createdByUserName || formatUserName(user);
        const updatedByUserName = note.updatedByUserName;

        // Show updatedBy if note was updated, otherwise show createdBy
        const displayUserName = updatedByUserName || createdByUserName;

        const noteTooltipContent = getNoteTooltipContent({
          t,
          updatedByUserName,
          createdByUserName,
          createdAt: note.creationDate,
          updatedAt: note.userUpdatedAt
        });

        return (
          <Tooltip
            title={noteTooltipContent}
            id="note_header_tooltip"
            placement="top"
            arrow={{
              pointAtCenter: true
            }}
            overlayInnerStyle={{
              backgroundColor: GRAY_900,
              borderRadius: "8px",
              fontWeight: fontWeights.semibold,
              fontSize: sizes.small,
              color: WHITE,
              whiteSpace: "pre-line"
            }}
            overlayStyle={{
              maxWidth: "400px"
            }}
          >
            <div id="note_header_label" style={{ cursor: "pointer", display: "inline-block" }}>
              {displayUserName}
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: t("patient.notes.col_title_cycle"),
      dataIndex: "cycle",
      key: "cycle",
      width: 100,
      sortDirections: SORT_DIRECTIONS,
      sorter: (a, b) => {
        const cycleA = a.cycle || 0;
        const cycleB = b.cycle || 0;
        return cycleA - cycleB;
      },
      render: (cycle) => cycle || ""
    },
    {
      title: t("patient.notes.col_title_admin_date"),
      dataIndex: "administrationDate",
      key: "administrationDate",
      width: 140,
      sortDirections: SORT_DIRECTIONS,
      sorter: (a, b, sortDirection) => dateSort(a.administrationDate, b.administrationDate, sortDirection),
      render: (date) => (date ? moment(date).format(DATE_FORMAT_2) : "")
    },
    {
      title: t("patient.notes.col_title_note"),
      dataIndex: "note",
      key: "note",
      ellipsis: false,
      render: (text, note) => {
        const isEditing = editingNoteId === note.id;

        if (isEditing) {
          const user = users[note.createdBy];
          const createdByUserName = note.createdByUserName || formatUserName(user);
          const updatedByUserName = note.updatedByUserName;

          const noteHeaderLabel = getNoteHeaderLabel({
            t,
            updatedByUserName,
            createdByUserName,
            updatedAt: note.userUpdatedAt,
            createdAt: note.creationDate
          });

          const noteTooltipContent = getNoteTooltipContent({
            t,
            updatedByUserName,
            createdByUserName,
            createdAt: note.creationDate,
            updatedAt: note.userUpdatedAt
          });

          return (
            <Form form={editForm}>
              <EditContainer>
                <EditNoteHeader>
                  <Tooltip
                    title={noteTooltipContent}
                    id="note_header_tooltip"
                    placement="top"
                    overlayInnerStyle={{
                      backgroundColor: GRAY_900,
                      borderRadius: "8px",
                      fontWeight: fontWeights.semibold,
                      fontSize: sizes.small,
                      color: WHITE,
                      whiteSpace: "pre-line"
                    }}
                    overlayStyle={{
                      maxWidth: "400px"
                    }}
                  >
                    <EditNoteLabel id="note_header_label">{noteHeaderLabel}</EditNoteLabel>
                  </Tooltip>
                </EditNoteHeader>

                <Form.Item name={`editNoteText-${note.id}`} initialValue={note.note} style={{ marginBottom: 0 }}>
                  <EditNoteTextArea
                    onChange={handleEditNoteTextChange}
                    placeholder={t("patient.notes.edit_note_placeholder")}
                    id="edit_note_text_area"
                  />
                </Form.Item>
              </EditContainer>
            </Form>
          );
        }

        return (
          <NoteContent id="note_content">
            <ExpandableNoteContent>{text}</ExpandableNoteContent>
          </NoteContent>
        );
      }
    },
    {
      title: t("patient.notes.actions"),
      key: "actions",
      width: 100,
      align: "right",
      render: (_, note) => {
        const isEditing = editingNoteId === note.id;

        if (isEditing) {
          return (
            <ButtonsContainer>
              <SaveNoteButton
                type="primary"
                onClick={handleSaveEdit}
                disabled={!hasUnsavedChanges || !editingNoteText.trim()}
                id="save_note_button"
              >
                {t("patient.notes.save_note")}
              </SaveNoteButton>
              <CancelEditButton type="link" onClick={handleCancelEdit} id="cancel_note_button">
                {t("cancel")}
              </CancelEditButton>
            </ButtonsContainer>
          );
        }

        return (
          <ActionButtons>
            <ActionButton
              type="text"
              icon={<EditNoteIcon />}
              onClick={() => handleEditNote(note)}
              id="edit_note_icon"
            />

            <ActionButton
              type="text"
              icon={<DeleteNoteIcon />}
              onClick={() => handleDeleteClick(note)}
              id="delete_note_icon"
            />
          </ActionButtons>
        );
      }
    }
  ];

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: "87vw", marginBottom: 20 }}>
        {isFormOpen ? (
          <Form
            name="newNote"
            form={form}
            initialValues={{
              note: "",
              cycle: getDefaultCycleNumber(selectedOrderId, cycles, patient.currentOrder)
            }}
            onFinish={onAddNote}
          >
            <Row align="middle" style={{ width: "100%", backgroundColor: "#F9FBFF", padding: 16 }}>
              <Col span={13} style={{ paddingRight: 16 }}>
                <NoteFormItem dataTMId="nt-note-entry" rows={1} style={{ marginBottom: 0 }} id="patient_note_input" />
              </Col>
              <Col span={11}>
                <Space>
                  <Radio.Group
                    buttonStyle="solid"
                    size="large"
                    name="noteType"
                    defaultValue={noteType}
                    onChange={(e) => setNoteType(e.target.value)}
                  >
                    <Radio data-tm-id="nt-per-patient-btn" value={NoteType.PATIENT}>
                      {t("form.cycle.per_patient")}
                    </Radio>
                    <Radio data-tm-id="nt-per-cycle-btn" value={NoteType.CYCLE}>
                      {t("form.cycle.per_cycle")}
                    </Radio>
                  </Radio.Group>
                  {noteType === NoteType.CYCLE && (
                    <CycleFormItem
                      required={true}
                      style={{ marginBottom: 0 }}
                      patient={patient}
                      dataTMId="nt-current-drop-down"
                    />
                  )}
                </Space>
              </Col>
            </Row>
            <Row justify="end" style={{ width: "100%", backgroundColor: "#F9FBFF", padding: "0px 16px 16px 16px" }}>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  id="cancel_note_button"
                  type="text"
                  onClick={() => {
                    form.resetFields();
                    setIsFormOpen(false);
                  }}
                >
                  <Text style={{ color: "#2B56A3" }}>{t("cancel")}</Text>
                </Button>
                <AddNoteButton
                  data-tm-id="nt-add-note-btn"
                  id="save_note_button"
                  htmlType="submit"
                  loading={isFetching}
                  type="primary"
                  disabled={editingNoteId !== null}
                >
                  {t("patient.notes.add_button")}
                </AddNoteButton>
              </Form.Item>
            </Row>
          </Form>
        ) : (
          <AddNoteButton
            data-tm-id="nt-add-note-btn"
            id="nt-add-note-btn"
            icon={<PlusOutlined style={{ fontSize: 12 }} />}
            onClick={() => setIsFormOpen(true)}
            type="primary"
            disabled={editingNoteId !== null}
          >
            {t("patient.notes.add_button")}
          </AddNoteButton>
        )}
      </Space>

      <StyledTable
        isFormOpen={isFormOpen}
        columns={columns}
        dataSource={displayedNotes}
        rowKey="id"
        pagination={false}
        loading={isFetching || isFetchingEditNote || isFetchingDeleteNote}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <StyledEmptyDescription>
                  <Text style={{ fontWeight: fontWeights.bold }}>{t("patient.notes.no_notes_yet")}</Text>
                  <Text>{t("patient.notes.start_by_typing_in_the_box_above")}</Text>
                </StyledEmptyDescription>
              }
            />
          )
        }}
        scroll={
          displayedNotes.length > 12 ? { y: isFormOpen ? "calc(100vh - 450px)" : "calc(100vh - 370px)" } : undefined
        }
        rowClassName={(record) => (editingNoteId === record.id ? "editing-row" : "")}
      />

      {contextHolder}
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  patientNotes: selectPatientNotes,
  users: selectUsers,
  user: selectUser,
  isFetching: selectIsFetching([ApiNames.NotesAdd])
});

const mapDispatchToProps = (dispatch) => ({
  addPatientNoteAct: (patientId, note, orderIndex, pharmacyPatient) =>
    dispatch(ACTIONS.addPatientNoteAction(patientId, note, orderIndex, pharmacyPatient)),
  editPatientNoteAct: (noteId, note, patientId, pharmacyPatient) =>
    dispatch(ACTIONS.editPatientNoteAction(noteId, note, patientId, pharmacyPatient)),
  deletePatientNoteAct: (patientId, noteId, pharmacyPatient) =>
    dispatch(ACTIONS.deletePatientNoteAction(patientId, noteId, pharmacyPatient))
});

const PatientNoteTab = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(PatientNoteTabComponent));

export { PatientNoteTab };
